import React, { useState, useEffect } from 'react';
import { API_BASE } from '../config';
import { useParams } from 'react-router-dom';

export default function DisplayMode() {
  const { code } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    const poll = () => {
      fetch(`${API_BASE}/display/${code}`)
        .then(r => r.ok ? r.json() : Promise.reject('Not found'))
        .then(d => { setData(d); setError(''); })
        .catch(() => setError('Room not found or invalid code.'));
    };
    poll();
    const interval = setInterval(poll, 2000);
    return () => clearInterval(interval);
  }, [code]);

  if (error) return <div className="display-mode"><div className="display-error">{error}</div></div>;
  if (!data) return <div className="display-mode"><div className="display-loading">⏳ Connecting...</div></div>;

  const { combat_active, players, enemies, order, active_turn, round, log } = data;

  return (
    <div className="display-mode">
      <div className="display-particles" />

      {/* Header */}
      <div className="display-header">
        <span className="display-title">{data.room_name || '⚔️ Lorewalker Combat'}</span>
        {combat_active && <span className="display-round">Round {round}</span>}
      </div>

      {!combat_active ? (
        <div className="display-idle">
          <div className="display-idle-icon">🧙‍♂️</div>
          <div className="display-idle-text">Awaiting combat...</div>
          <div className="display-players-idle">
            {players.map((p, i) => (
              <span key={i} className="display-player-tag">{p.char_name} <small>Lv{p.level} {p.class}</small></span>
            ))}
          </div>
        </div>
      ) : (
        <div className="display-combat">
          {/* Initiative Bar */}
          <div className="display-initiative">
            {order.map((o, i) => (
              <div key={i} className={`init-token ${o.type === 'player' ? 'init-player' : 'init-enemy'} ${o.id === active_turn || o.name === active_turn ? 'init-active' : ''}`}>
                {o.name}
              </div>
            ))}
          </div>

          {/* Main Arena */}
          <div className="display-arena">
            {/* Players Side */}
            <div className="display-side display-players">
              {players.map((p, i) => {
                const hpPct = Math.max(0, (p.hp / Math.max(p.max_hp, 1)) * 100);
                const isDead = p.hp <= 0;
                return (
                  <div key={i} className={`display-card ${isDead ? 'display-dead' : ''}`}>
                    <div className="display-card-name">{p.char_name}</div>
                    <div className="display-card-class">{p.class} · AC {p.ac}</div>
                    <div className="display-hp-bar">
                      <div className="display-hp-fill" style={{ width: `${hpPct}%`, background: hpPct > 50 ? '#2ecc71' : hpPct > 25 ? '#f39c12' : '#e74c3c' }} />
                    </div>
                    <div className="display-hp-text">{p.hp}/{p.max_hp}</div>
                    {p.conditions_active && <div className="display-conditions">{p.conditions_active}</div>}
                    {isDead && <div className="display-skull">💀</div>}
                  </div>
                );
              })}
            </div>

            {/* VS Divider */}
            <div className="display-vs">⚔️</div>

            {/* Enemies Side */}
            <div className="display-side display-enemies">
              {enemies.map((e, i) => {
                const hpPct = Math.max(0, (e.hp / Math.max(e.max_hp, 1)) * 100);
                return (
                  <div key={i} className="display-card display-enemy-card">
                    <div className="display-card-name">{e.name}</div>
                    <div className="display-card-class">AC {e.ac}</div>
                    <div className="display-hp-bar">
                      <div className="display-hp-fill enemy-hp" style={{ width: `${hpPct}%` }} />
                    </div>
                    <div className="display-hp-text">{e.hp}/{e.max_hp}</div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Combat Log */}
          <div className="display-log">
            {log.slice(-5).map((l, i) => (
              <div key={i} className="display-log-line">{l}</div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
